import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Button, Tooltip } from '@mui/material';
import ForbiddenTooltip from 'components/general/ForbiddenTooltip/ForbiddenTooltip';
import { formatTranslation } from 'intl';
import { AnalyticsTranslationKeys, FiltersTranslationKeys } from 'intl/keys';
import React, { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { PermissionTranslationKeys } from 'shared/domain/permissions/translationKeys';
import { isNonEmptyArray } from 'shared/utils/array';
import { MemoSectionFilterChip } from './SectionFilterChip';
import type { SectionSharedFiltersModel } from './useSectionSharedFilters';
import { sectionFiltersClearSx, sectionFiltersMixedInfoSx, sectionFiltersRowSx } from './styles';

type SectionFiltersProps = Readonly<{
  model: SectionSharedFiltersModel;
}>;

/**
 * The filters of a dashboard section as a row of chips (PT-6804). A change
 * applies at once to the charts that follow the section; charts with their
 * own filters keep them.
 */
export function SectionFilters({ model }: SectionFiltersProps): ReactElement | null {
  const intl = useIntl();
  const { states, setValue, clear, clearAll, activeFilters, isMixed, disabled } = model;

  if (!isNonEmptyArray(states)) {
    return null;
  }

  const sectionFiltersLabel = formatTranslation(intl, AnalyticsTranslationKeys.sectionFiltersLabel);
  const mixedInfo = formatTranslation(intl, AnalyticsTranslationKeys.sectionFiltersMixed);

  return (
    <ForbiddenTooltip visibleWhen={disabled} reason={PermissionTranslationKeys.OFFLINE_UNAVAILABLE}>
      <Box sx={sectionFiltersRowSx} role='region' aria-label={sectionFiltersLabel}>
        {states.map((state) => (
          <MemoSectionFilterChip
            key={state.shared}
            state={state}
            isActive={activeFilters.has(state.shared)}
            disabled={disabled}
            onChange={setValue}
            onClear={clear}
          />
        ))}
        {activeFilters.size > 0 ? (
          <Button size='small' onClick={clearAll} disabled={disabled} sx={sectionFiltersClearSx}>
            {formatTranslation(intl, FiltersTranslationKeys.clearAll)}
          </Button>
        ) : null}
        {isMixed ? (
          <Tooltip title={mixedInfo}>
            <InfoOutlinedIcon sx={sectionFiltersMixedInfoSx} aria-label={mixedInfo} />
          </Tooltip>
        ) : null}
      </Box>
    </ForbiddenTooltip>
  );
}

export const MemoSectionFilters = React.memo(SectionFilters);
